import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Loading, Message, Text } from '@workwhile/ui'
import { GroupedVirtuoso } from 'react-virtuoso'
import { AdminCharge, AdminChargeGroup } from 'api/admin/charges/getCharges'
import { useChargesContextValue } from '../context/ChargesProvider'
import { ChargeCard } from './ChargeCard'
import { ChargeGroupHeader } from './ChargeGroupHeader'
import { ChargesPagination } from './ChargesPagination'

function getChargeFromGroups(
  groups: AdminChargeGroup[],
  groupIndex: number,
  flatIndex: number,
  groupCounts: number[]
): AdminCharge | null {
  const totalInPreviousGroups = groupCounts
    .slice(0, groupIndex)
    .reduce((count, groupCount) => count + groupCount, 0)
  const localIndex = flatIndex - totalInPreviousGroups
  const group = groups[groupIndex]
  if (group == null) {
    return null
  }
  return group.charges[localIndex] ?? null
}

function getChargeByFlatIndex(
  groups: AdminChargeGroup[],
  flatIndex: number,
  groupCounts: number[]
): AdminCharge | null {
  let remaining = flatIndex
  for (let index = 0; index < groups.length; index += 1) {
    const visibleCount = groupCounts[index] ?? 0
    if (remaining < visibleCount) {
      return groups[index]?.charges[remaining] ?? null
    }
    remaining -= visibleCount
  }
  return null
}

function groupKey(group: AdminChargeGroup, index: number): string {
  if (group.kind === 'invoice' && group.invoice != null) {
    return `invoice-${group.invoice.id}`
  }
  if (group.kind === 'uninvoiced' && group.uninvoiced != null) {
    const { stagingStatus, runAt, invoiceGroupKey } = group.uninvoiced
    const statusBucket =
      stagingStatus === 'not_ready' ? 'not_ready' : 'eligible'
    return `uninvoiced-${statusBucket}-${runAt ?? 'none'}-${invoiceGroupKey.join('|')}`
  }
  return `uninvoiced-${index}`
}

export const ChargesList = () => {
  const {
    state: { data, hasIdentityFilter, isPending, isError, error },
  } = useChargesContextValue()

  if (!hasIdentityFilter) {
    return (
      <Message variant="info" borderRadius="small">
        Select a company or enter a shift / worker / invoice ID to load charges.
      </Message>
    )
  }

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (isError && data != null && error) {
    return (
      <>
        <Message variant="error" mb={3} borderRadius="small">
          {error}
        </Message>
        <ChargesListBody />
      </>
    )
  }

  return <ChargesListBody />
}

const ChargesListBody = () => {
  const {
    state: {
      data,
      companyId,
      shiftId,
      workerId,
      invoiceId,
      invoiced,
      pagination,
    },
  } = useChargesContextValue()

  // Admin layout scrolls inside <main class="main-container">, not the window.
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null)
  useEffect(() => {
    const main = document.querySelector('main.main-container')
    setScrollParent(main instanceof HTMLElement ? main : null)
  }, [])

  const groups = data?.items ?? []

  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState(
    () => new Set<string>()
  )

  // Reset collapse state when the loaded page of groups changes.
  useEffect(() => {
    setCollapsedGroupKeys(new Set())
  }, [
    companyId,
    shiftId,
    workerId,
    invoiceId,
    invoiced,
    pagination.pageIndex,
    pagination.pageSize,
  ])

  const handleToggleGroup = useCallback((key: string) => {
    setCollapsedGroupKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const groupCounts = useMemo(
    () =>
      groups.map((group, index) => {
        const key = groupKey(group, index)
        if (collapsedGroupKeys.has(key)) {
          return 0
        }
        return group.charges.length
      }),
    [groups, collapsedGroupKeys]
  )

  const listKey = useMemo(
    () =>
      [
        companyId ?? 'all',
        shiftId ?? 'all',
        workerId ?? 'all',
        invoiceId ?? 'all',
        invoiced ?? 'all',
        pagination.pageIndex,
        pagination.pageSize,
      ].join('|'),
    [
      companyId,
      shiftId,
      workerId,
      invoiceId,
      invoiced,
      pagination.pageIndex,
      pagination.pageSize,
    ]
  )

  const itemContent = useCallback(
    (flatIndex: number, groupIndex: number) => {
      const charge = getChargeFromGroups(
        groups,
        groupIndex,
        flatIndex,
        groupCounts
      )
      if (charge == null) {
        return null
      }
      return (
        <Box pl={2} mt={2}>
          <ChargeCard charge={charge} />
        </Box>
      )
    },
    [groups, groupCounts]
  )

  const computeItemKey = useCallback(
    (flatIndex: number) => {
      const charge = getChargeByFlatIndex(groups, flatIndex, groupCounts)
      return charge?.id ?? `idx-${flatIndex}`
    },
    [groups, groupCounts]
  )

  if (data == null) {
    return null
  }

  if (data.items.length === 0) {
    return (
      <Text color="lightText">No charges found for the selected filters.</Text>
    )
  }

  if (scrollParent == null) {
    return null
  }

  return (
    <GroupedVirtuoso
      key={listKey}
      customScrollParent={scrollParent}
      groupCounts={groupCounts}
      groupContent={(index) => {
        const group = groups[index]
        if (group == null) {
          return null
        }
        const key = groupKey(group, index)
        return (
          <ChargeGroupHeader
            group={group}
            isExpanded={!collapsedGroupKeys.has(key)}
            onToggle={() => handleToggleGroup(key)}
          />
        )
      }}
      itemContent={itemContent}
      computeItemKey={computeItemKey}
      components={{
        Footer: ChargesPagination,
      }}
    />
  )
}
